import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { CriticalPointAiService } from "./critical-point-ai-service";

/**
 * State interface for the AI popover.
 */
export interface CriticalPointAiPopoverState {
  generatedDescriptions: string[];
  currentDescriptionIndex: number;
  baseDescription: string;
  isRewriting: boolean;
}

/**
 * Service that manages the state and operations for the critical point AI popover.
 * This service handles:
 * - State persistence across popover open/close cycles
 * - AI generation requests (find and rewrite)
 * - Navigation between generated descriptions
 * - State clearing when appropriate
 */
@Injectable({
  providedIn: "root",
})
export class CriticalPointAiPopoverStateService {
  private readonly stateSubject = new BehaviorSubject<CriticalPointAiPopoverState | null>(null);

  constructor(private criticalPointAiService: CriticalPointAiService) {}

  /**
   * Observable of the current state.
   */
  public get state$(): Observable<CriticalPointAiPopoverState | null> {
    return this.stateSubject.asObservable();
  }

  /**
   * Returns the current state synchronously.
   */
  public getState(): CriticalPointAiPopoverState | null {
    return this.stateSubject.getValue();
  }

  /**
   * Initializes the state with a base description.
   * Call this when opening the popover if no state exists.
   */
  public initializeState(baseDescription: string): void {
    if (this.stateSubject.getValue() !== null) {
      return; // State already exists
    }

    this.stateSubject.next({
      generatedDescriptions: [baseDescription],
      currentDescriptionIndex: 0,
      baseDescription,
      isRewriting: false,
    });
  }

  /**
   * Clears all state. Call this when:
   * - The dialog closes
   * - The user accepts a description
   * - The base description changes externally
   */
  public clearState(): void {
    this.stateSubject.next(null);
  }

  /**
   * Checks if there is an active state.
   */
  public hasState(): boolean {
    return this.stateSubject.getValue() !== null;
  }

  /**
   * Checks if a generation is currently in progress.
   */
  public isLoading(): boolean {
    const state = this.stateSubject.getValue();
    return state?.isRewriting === true;
  }

  /**
   * Updates the description at the current index.
   * Also updates baseDescription if we're at index 0.
   */
  public updateCurrentDescription(newValue: string): void {
    const state = this.stateSubject.getValue();
    if (!state) return;

    const newDescriptions = [...state.generatedDescriptions];
    newDescriptions[state.currentDescriptionIndex] = newValue;

    const newBaseDescription = state.currentDescriptionIndex === 0
      ? newValue
      : state.baseDescription;

    this.stateSubject.next({
      ...state,
      generatedDescriptions: newDescriptions,
      baseDescription: newBaseDescription,
    });
  }

  /**
   * Navigates to the previous description if available.
   * Returns true if navigation occurred.
   */
  public showPreviousDescription(): boolean {
    const state = this.stateSubject.getValue();
    if (!state || state.generatedDescriptions.length === 0) {
      return false;
    }

    const previousIndex = state.currentDescriptionIndex - 1;
    if (previousIndex < 0) {
      return false;
    }

    this.stateSubject.next({
      ...state,
      currentDescriptionIndex: previousIndex,
    });
    return true;
  }

  /**
   * Navigates to the next description if available.
   * Returns true if navigation occurred.
   */
  public showNextDescription(): boolean {
    const state = this.stateSubject.getValue();
    if (!state || state.generatedDescriptions.length === 0) {
      return false;
    }

    const nextIndex = state.currentDescriptionIndex + 1;
    if (nextIndex >= state.generatedDescriptions.length) {
      return false;
    }

    this.stateSubject.next({
      ...state,
      currentDescriptionIndex: nextIndex,
    });
    return true;
  }

  /**
   * Gets the currently displayed description.
   */
  public getCurrentDescription(): string {
    const state = this.stateSubject.getValue();
    if (!state) return "";
    return state.generatedDescriptions[state.currentDescriptionIndex] ?? state.baseDescription;
  }

  /**
   * Requests AI to rewrite the base description.
   * Optionally includes an image for context.
   */
  public async rewriteDescription(getImage?: () => Promise<string | undefined>): Promise<void> {
    const state = this.stateSubject.getValue();
    if (!state || state.isRewriting) {
      return;
    }

    const baseDescription = (state.baseDescription ?? "").trim();
    if (!baseDescription) {
      return;
    }

    // Set loading state
    this.stateSubject.next({
      ...state,
      isRewriting: true,
    });

    try {
      let image: string | undefined;
      if (getImage) {
        image = await getImage();
      }

      const results = await this.criticalPointAiService.rewriteCriticalPointDescription(
        baseDescription,
        image
      );
      this.applyGeneratedResults(results);
    } finally {
      const currentState = this.stateSubject.getValue();
      if (currentState) {
        this.stateSubject.next({
          ...currentState,
          isRewriting: false,
        });
      }
    }
  }

  /**
   * Applies the results from AI generation to the state.
   */
  private applyGeneratedResults(results: string[] | undefined): void {
    const state = this.stateSubject.getValue();
    if (!state) return;

    const normalizedDescriptions = Array.isArray(results)
      ? results.filter((d): d is string => typeof d === "string")
      : [];

    if (normalizedDescriptions.length === 0) {
      // No results, keep only the base description
      this.stateSubject.next({
        ...state,
        generatedDescriptions: [state.baseDescription],
        currentDescriptionIndex: 0,
      });
      return;
    }

    const newDescriptions = [state.baseDescription, ...normalizedDescriptions];
    const nextIndex = Math.min(
      Math.max(1, state.currentDescriptionIndex),
      newDescriptions.length - 1
    );

    this.stateSubject.next({
      ...state,
      generatedDescriptions: newDescriptions,
      currentDescriptionIndex: nextIndex,
    });
  }
}
